import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import RetryIcon from '@mui/icons-material/ReplayRounded';
import { useTranslation } from '../../i18n';
import { agentErrorKey, type AgentErrorContext, type AgentErrorInfo } from '../../lib/agentErrors';

// An error in the agent chat: a friendly localized line, the raw error as
// collapsible detail, and a Retry button (disabled while the retry runs).
export function AgentErrorNotice({
  context,
  error,
  onRetry,
  retrying,
  disabled,
  action,
  onClose,
}: {
  context: AgentErrorContext;
  error: AgentErrorInfo;
  onRetry?: () => void;
  retrying?: boolean;
  disabled?: boolean;
  // Extra action shown before Retry (e.g. Buy credits).
  action?: ReactNode;
  onClose?: () => void;
}) {
  const t = useTranslation().videoAgent;
  const [open, setOpen] = useState(false);
  const headline = t[agentErrorKey(context, error)];
  const detail = error.raw && error.raw !== headline ? error.raw : '';

  return (
    <Alert
      severity="error"
      onClose={onClose}
      sx={{ alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}
      data-testid="agent-error"
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {headline}
      </Typography>
      {detail && (
        <>
          <Button
            size="small"
            color="inherit"
            onClick={() => setOpen((v) => !v)}
            sx={{ px: 0, minWidth: 0, mt: 0.25, fontSize: 12, opacity: 0.8, textTransform: 'none' }}
          >
            {t.errorDetails}
          </Button>
          <Collapse in={open}>
            <Box
              component="pre"
              sx={{ m: 0, mt: 0.5, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', opacity: 0.85, fontFamily: 'monospace' }}
            >
              {detail}
            </Box>
          </Collapse>
        </>
      )}
      {(onRetry || action) && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {action}
          {onRetry && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={onRetry}
              disabled={retrying || disabled}
              startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <RetryIcon fontSize="small" />}
            >
              {retrying ? t.retrying : t.retry}
            </Button>
          )}
        </Stack>
      )}
    </Alert>
  );
}
